import type { ManagedPersonStatus } from "#shared/managePeopleContract.ts";
import { Button, type ButtonProps } from "@/components/ui/button.tsx";
import { ArrowPathIcon, NoSymbolIcon, PaperAirplaneIcon, TrashIcon } from "@heroicons/react/20/solid";
import { useI18nextInstance } from "@squide/i18next";
import type { ComponentType, SVGProps } from "react";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { i18NextInstanceKey } from "../../../i18n/i18next.ts";
import { type PersonHeaderAction, resolvePersonHeaderActions } from "./personDetailHeaderActions.ts";

const ACTION_ICONS: Record<PersonHeaderAction, ComponentType<SVGProps<SVGSVGElement>>> = {
    deactivate: NoSymbolIcon,
    reactivate: ArrowPathIcon,
    sendInvite: PaperAirplaneIcon,
    resendInvite: PaperAirplaneIcon,
    remove: TrashIcon
};

const ACTION_INTENTS: Record<PersonHeaderAction, ButtonProps["intent"]> = {
    deactivate: "outline",
    reactivate: "outline",
    sendInvite: "primary",
    resendInvite: "outline",
    remove: "outline"
};

const DELETE_CLASSNAME = "border-danger text-danger hover:text-danger [--btn-icon-active:var(--color-danger)] [--btn-icon:var(--color-danger)] [--btn-outline:var(--color-danger)] [--btn-overlay:var(--color-danger-subtle)]";

const ACTION_NAMES: Record<PersonHeaderAction, string> = {
    deactivate: "deactivate-user",
    reactivate: "reactivate-user",
    sendInvite: "send-invite",
    resendInvite: "resend-invite",
    remove: "delete-user"
};

interface PersonDetailHeaderActionButtonProps {
    action: PersonHeaderAction;
    label: string;
    isPending: boolean;
    isDisabled: boolean;
    onAction: (action: PersonHeaderAction) => void;
}

function PersonDetailHeaderActionButton({ action, label, isPending, isDisabled, onAction }: PersonDetailHeaderActionButtonProps) {
    const Icon = ACTION_ICONS[action];

    const handlePress = useCallback(() => onAction(action), [onAction, action]);

    return (
        <Button intent={ACTION_INTENTS[action]} className={action === "remove" ? DELETE_CLASSNAME : undefined} name={ACTION_NAMES[action]} isPending={isPending} isDisabled={isDisabled} onPress={handlePress}>
            <Icon aria-hidden="true" />
            {label}
        </Button>
    );
}

export interface PersonDetailHeaderActionsProps {
    status: ManagedPersonStatus;
    isSelf: boolean;
    pendingAction: PersonHeaderAction | undefined;
    onAction: (action: PersonHeaderAction) => void;
}

export function PersonDetailHeaderActions({ status, isSelf, pendingAction, onAction }: PersonDetailHeaderActionsProps) {
    const i18nextInstance = useI18nextInstance(i18NextInstanceKey);
    const { t } = useTranslation("PersonDetailHeaderActions", { i18n: i18nextInstance });

    const actions = resolvePersonHeaderActions(status, isSelf);

    if (actions.length === 0) {
        return null;
    }

    return (
        <div className="flex flex-wrap items-center gap-2 sm:flex-nowrap">
            {actions.map(action => (
                <PersonDetailHeaderActionButton key={action === "remove" ? "remove" : "primary"} action={action} label={t(action)} isPending={pendingAction === action} isDisabled={pendingAction !== undefined && pendingAction !== action} onAction={onAction} />
            ))}
        </div>
    );
}
